import React from "react";
import { Droplets, Flame, Biohazard, Hammer, ShieldCheck, Wrench, Clock, CircleCheck as CheckCircle2, FileCheck2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
export default function FrederickServices() {
  return <section id="frederickcountyservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted text-muted-foreground text-xs font-semibold mb-4">
            <Clock className="w-3.5 h-3.5 text-primary" />
            <span>~60-Minute Mobile Deployment</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
            Comprehensive Property Restoration Services Across Frederick County
          </h2>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            From sudden pipe bursts during winter freezes along the Catoctin Ridge to heavy thunderstorm
            flood surges near the Monocacy River and electrical fires in historic downtown Frederick brick
            structures, TriServe delivers rapid, experienced emergency response. We operate without
            third-party call centers: your call to 855-435-6631 connects directly with our certified local field
            technicians. Staged along the US-15 and I-70 corridors, our mobile response units arrive equipped
            for immediate stabilization anywhere in Frederick County. We operate exclusively as a mobile
            emergency service with no walk-in office in Maryland.
          </p>
        </div>

        {/* Operational Commitments Banner */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6 rounded-xl bg-muted/60 border border-border mb-16">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-foreground text-base">Direct Local Crews</h3>
              <p className="text-sm text-muted-foreground mt-1">
                You speak directly to the technicians deploying to your property, guaranteeing real-time
                accountability, clear communication, and rapid containment without phone trees or call centers.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-foreground text-base">~60-Minute Mobile Dispatch</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Field trucks staged near the US-15 and I-70 interchange deploy within an estimated 60 minutes.
                We provide full on-site mobile response throughout Frederick County with no walk-in office in Maryland.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <FileCheck2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-foreground text-base">Direct Insurance Coordination</h3>
              <p className="text-sm text-muted-foreground mt-1">
                We manage infrared moisture logs, photographic inventories, and standard itemized claim filings
                directly with your insurance carrier and adjuster to accelerate claims processing.
              </p>
            </div>
          </div>
        </div>

        {/* Main Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Service 1: Water Damage & Flood Cleanup */}
          <Card className="flex flex-col justify-between border-border bg-card shadow-xs hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-2">
                <Droplets className="w-5 h-5" />
              </div>
              <CardTitle className="text-xl font-bold font-default">
                <h3>Water Damage & Flood Cleanup</h3>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col justify-between flex-grow gap-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Flash floods along the Monocacy River basin, seasonal runoff from Catoctin Mountain, and plumbing
                failures push thousands of gallons into basements and commercial floors. TriServe mobilizes
                truck-mounted water extractors, commercial desiccant dehumidifiers, and thermal imaging cameras to
                locate concealed moisture behind historic plaster and subflooring before structural rot takes hold.
              </p>
              <Button asChild variant="outline" className="w-full mt-auto">
                <Link to="/WaterDamageFloodRestorationPage">
                  Water Damage & Flood Restoration
                </Link>
              </Button>
            </CardContent>
          </Card>

          {/* Service 2: Sewage Backup Cleanup */}
          <Card className="flex flex-col justify-between border-border bg-card shadow-xs hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-2">
                <Biohazard className="w-5 h-5" />
              </div>
              <CardTitle className="text-xl font-bold font-default">
                <h3>Sewage Backup Cleanup</h3>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col justify-between flex-grow gap-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Municipal sewer line overflows, heavy stormwater backflow, and septic line blockages introduce
                Category 3 blackwater loaded with dangerous pathogens into living spaces. Our certified response crews
                establish strict negative-pressure containment, extract contaminated liquid, safely remove compromised
                porous building materials, and sanitize surfaces with hospital-grade antimicrobial treatments.
              </p>
              <Button asChild variant="outline" className="w-full mt-auto">
                <Link to="/SewageCleanupPage">
                  Sewage Cleanup
                </Link>
              </Button>
            </CardContent>
          </Card>

          {/* Service 3: Fire & Smoke Damage Restoration */}
          <Card className="flex flex-col justify-between border-border bg-card shadow-xs hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-2">
                <Flame className="w-5 h-5" />
              </div>
              <CardTitle className="text-xl font-bold font-default">
                <h3>Fire & Smoke Damage Restoration</h3>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col justify-between flex-grow gap-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Structural fires in older downtown Frederick properties and suburban developments leave behind heavy
                soot, acidic smoke residue, and extensive fire-hose water damage. We provide rapid emergency board-up
                and roof tarping, structural stabilization, and soot removal, neutralizing deep odors in timber,
                brickwork, and duct systems with industrial hydroxyl and thermal fogging equipment.
              </p>
              <Button asChild variant="outline" className="w-full mt-auto">
                <Link to="/FireSmokeOdorRestorationPage">
                  Fire & Smoke
                </Link>
              </Button>
            </CardContent>
          </Card>

          {/* Service 4: Mold Remediation */}
          <Card className="flex flex-col justify-between border-border bg-card shadow-xs hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-2">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <CardTitle className="text-xl font-bold font-default">
                <h3>Mold Remediation</h3>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col justify-between flex-grow gap-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Elevated summer humidity across the Frederick Valley coupled with unaddressed cellar dampness feeds
                aggressive microbial growth within crawlspaces, subfloors, and wall cavities. TriServe isolates
                colonies using physical poly containment barriers, deploys HEPA air scrubbers to capture airborne
                spores, safely cleans affected structural members, and resolves root moisture sources.
              </p>
              <Button asChild variant="outline" className="w-full mt-auto">
                <Link to="/MoldRemediationPage">
                  Mold Remediation
                </Link>
              </Button>
            </CardContent>
          </Card>

          {/* Service 5: Reconstruction & Build-Back */}
          <Card className="flex flex-col justify-between border-border bg-card shadow-xs hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-2">
                <Hammer className="w-5 h-5" />
              </div>
              <CardTitle className="text-xl font-bold font-default">
                <h3>Reconstruction & Build-Back</h3>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col justify-between flex-grow gap-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Following disaster mitigation, TriServe provides turnkey structural rebuilding. Our reconstruction
                team handles structural framing, subfloor replacement, insulation, drywall hanging, custom trim, and
                cabinetry installation. Seamlessly transitioning from emergency mitigation into full build-back removes
                contractor delays and maintains single-source accountability for your insurance claim.
              </p>
              <Button asChild variant="outline" className="w-full mt-auto">
                <Link to="/ReconstructionDesignBuildPage">
                  Reconstruction
                </Link>
              </Button>
            </CardContent>
          </Card>

          {/* Service 6: Basement Waterproofing */}
          <Card className="flex flex-col justify-between border-border bg-card shadow-xs hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-2">
                <Droplets className="w-5 h-5" />
              </div>
              <CardTitle className="text-xl font-bold font-default">
                <h3>Basement Waterproofing</h3>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col justify-between flex-grow gap-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Dense clay soils prevalent throughout Frederick County expand dramatically during prolonged rainstorms,
                generating intense hydrostatic pressure against stone and masonry foundation walls. We engineer interior
                perimeter drainage systems, multi-stage sump pumps with heavy-duty battery backups, and vapor barrier
                encapsulations designed to keep lower levels permanently dry.
              </p>
              <Button asChild variant="outline" className="w-full mt-auto">
                <Link to="/ContactRequestServicePage">
                  Request Waterproofing Inspection
                </Link>
              </Button>
            </CardContent>
          </Card>

          {/* Service 7: General Emergency Property Restoration */}
          <Card className="flex flex-col justify-between border-border bg-card shadow-xs hover:shadow-md transition-shadow md:col-span-2 lg:col-span-3">
            <CardHeader>
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-2">
                <Wrench className="w-5 h-5" />
              </div>
              <CardTitle className="text-xl font-bold font-default">
                <h3>24/7 Emergency Property Restoration & Plumbing Line Stabilization</h3>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col md:flex-row items-stretch justify-between gap-6">
              <div className="flex flex-col justify-between items-start w-full md:w-1/2 gap-6">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  When catastrophic water main ruptures, frozen pipes in Catoctin Ridge foothills, or commercial boiler
                  failures strike, immediate action halts progressive destruction. Call 855-435-6631 anytime day or night.
                  TriServe dispatches mobile response crews with an estimated 60-minute arrival window along the US-15 and
                  I-70 corridors, coordinating line shut-off, emergency extraction, and direct insurance documentation.
                </p>
                <Button asChild className="shrink-0">
                  <Link to="/ContactRequestServicePage">
                    Dispatch Emergency Crew
                  </Link>
                </Button>
              </div>
              <div className="w-full md:w-1/2 overflow-hidden rounded-lg border border-border min-h-[240px]">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/frederick_emergency_water_restoration_crew.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/frederick_emergency_water_restoration_crew.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/frederick_emergency_water_restoration_crew.png-wvc-sizes" alt="TriServe restoration technicians performing emergency water extraction and pipe rupture cleanup with professional drying equipment in a Frederick County, MD property" loading="lazy" decoding="async" width={600} height={400} className="w-full h-full object-cover rounded-lg" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}